import React, {useMemo} from 'react';
import {Highlight} from '@mantine/core';

interface HtmlHighlightRendererProps {
    html: string;
    highlight: string | string[];
    className?: string;
}

const ATTRIBUTE_NAME_MAP: Record<string, string> = {
    class: 'className',
    for: 'htmlFor',
    tabindex: 'tabIndex',
    readonly: 'readOnly',
    colspan: 'colSpan',
    rowspan: 'rowSpan',
    maxlength: 'maxLength',
    minlength: 'minLength',
    contenteditable: 'contentEditable',
};

const BLOCKED_TAGS = new Set(['script', 'style', 'iframe', 'object', 'embed']);

const toCamelCase = (value: string): string =>
    value.replace(/-([a-z])/g, (_, char: string) => char.toUpperCase());

const parseStyle = (styleValue: string): React.CSSProperties => {
    const style: Record<string, string> = {};

    for (const declaration of styleValue.split(';')) {
        const trimmed = declaration.trim();
        if (!trimmed) {
            continue;
        }

        const separatorIndex = trimmed.indexOf(':');
        if (separatorIndex === -1) {
            continue;
        }

        const property = trimmed.slice(0, separatorIndex).trim();
        const value = trimmed.slice(separatorIndex + 1).trim();
        if (!property || !value) {
            continue;
        }

        style[toCamelCase(property)] = value;
    }

    return style as React.CSSProperties;
};

const mapAttributeName = (name: string): string => ATTRIBUTE_NAME_MAP[name] ?? name;

const parseAttributes = (element: Element): Record<string, unknown> => {
    const props: Record<string, unknown> = {};
    for (const attribute of Array.from(element.attributes)) {
        const lowerName = attribute.name.toLowerCase();

        if (lowerName.startsWith('on')) {
            continue;
        }

        const mappedName = mapAttributeName(lowerName);
        if (mappedName === 'style') {
            props.style = parseStyle(attribute.value);
            continue;
        }

        props[mappedName] = attribute.value;
    }
    return props;
};

const renderNode = (
    node: ChildNode,
    key: string,
    highlight: string | string[],
): React.ReactNode => {
    if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent ?? '';
        if (!text) {
            return null;
        }

        return (
            <Highlight key={key} highlight={highlight} component="span">
                {text}
            </Highlight>
        );
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
        return null;
    }

    const element = node as Element;
    const tagName = element.tagName.toLowerCase();
    if (BLOCKED_TAGS.has(tagName)) {
        return null;
    }

    const children = Array.from(element.childNodes)
        .map((child, index) => renderNode(child, `${key}-${index}`, highlight))
        .filter(Boolean);

    return React.createElement(
        tagName,
        {
            key,
            ...parseAttributes(element),
        },
        children.length > 0 ? children : undefined,
    );
};

const HtmlHighlightRenderer: React.FC<HtmlHighlightRendererProps> = ({html, highlight, className}) => {
    const renderedContent = useMemo(() => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        return Array.from(doc.body.childNodes)
            .map((node, index) => renderNode(node, `node-${index}`, highlight))
            .filter(Boolean);
    }, [highlight, html]);

    return <div className={className}>{renderedContent}</div>;
};

export default HtmlHighlightRenderer;
